import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { FormControlLabel, Switch } from '@mui/material'
import type { GridFilterItem } from '@mui/x-data-grid'
import { FilterPanelTrigger, gridFilterModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import GridSearchOutlined from '@/components/dataDisplay/GridSearchOutlined'
import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import type { UserRole } from '@/constants/userRoles'
import { getFilterItemValue, updateDataGridFilter } from '@/utils/datagrid/filters'

import RoleFilter from './datagrid/RoleFilter'
import SitesFilter from './datagrid/SitesFilter'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends UsersDataGridToolbarProps {}
}

type UsersDataGridToolbarProps = {
  showDisabledUsers: boolean
  authorizedRoles: UserRole[]
  availableSiteOptions: string[]
  showSiteFilter: boolean
  showAddUserButton: boolean
  onToggleDisabledUsersClick: (value: boolean) => void
}

const ROLE_FILTER_NAME = 'role'
const ALLOW_RO_IDS_FILTER_NAME = 'allowedRoIds'

function UsersDataGridToolbar({
  showDisabledUsers,
  onToggleDisabledUsersClick,
  authorizedRoles,
  availableSiteOptions,
  showSiteFilter,
  showAddUserButton,
}: Readonly<UsersDataGridToolbarProps>) {
  const { t } = useTranslation()
  const apiRef = useGridApiContext()

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const roleFilterValue = getFilterItemValue<UserRole>(filterModel, ROLE_FILTER_NAME) ?? null
  const selectedSites = getFilterItemValue<string[]>(filterModel, ALLOW_RO_IDS_FILTER_NAME) ?? []

  function handleRoleFilterChange(item: GridFilterItem) {
    updateDataGridFilter(apiRef, ROLE_FILTER_NAME, item)
  }

  function handleDisabledFilterToggle(evt: ChangeEvent<HTMLInputElement>) {
    onToggleDisabledUsersClick(evt.target.checked)
  }

  function handleSelectedSitesFilter(item: GridFilterItem) {
    updateDataGridFilter(apiRef, ALLOW_RO_IDS_FILTER_NAME, item)
  }

  return (
    <GridToolbar
      actions={
        showAddUserButton ? (
          <CustomIconButton
            Icon={AddOutlinedIcon}
            aria-label={t('users.button_add_new_user')}
            color="primary"
            component={Link}
            to="/users/create"
            variant="solid"
          />
        ) : undefined
      }
      filters={
        <>
          <GridSearchOutlined label={t('common.navigation.search')} placeholder={t('user.search.placeholder')} />

          <RoleFilter
            field="role"
            roleOptions={authorizedRoles}
            value={roleFilterValue}
            onChange={(items) => handleRoleFilterChange(items[0])}
          />

          {showSiteFilter && (
            <SitesFilter
              field={ALLOW_RO_IDS_FILTER_NAME}
              id="selected-customers"
              siteOptions={availableSiteOptions}
              value={selectedSites}
              onChange={(items) => handleSelectedSitesFilter(items[0])}
            />
          )}

          <FormControlLabel
            control={<Switch checked={showDisabledUsers} onChange={handleDisabledFilterToggle} />}
            label={t('component.filter.show_disabled')}
            sx={{ m: 0 }}
          />

          <FilterPanelTrigger />
        </>
      }
    />
  )
}

export default UsersDataGridToolbar
